import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { subscribeInAppPush, type InAppPushData } from '../../utils/inAppPush';
import type { NotificationSeverity } from '../../types/notification.types';
import './InAppPushHost.css';

const MAX_STACK = 5;
const DURATION_INFO_MS = 8000;
const DURATION_IMPORTANT_MS = 12000;

function durationFor(sev: NotificationSeverity): number {
  return sev === 'error' || sev === 'warning' ? DURATION_IMPORTANT_MS : DURATION_INFO_MS;
}

export default function InAppPushHost() {
  const [items, setItems] = useState<InAppPushData[]>([]);

  useEffect(() => {
    return subscribeInAppPush((data) => {
      setItems((prev) => {
        const next = [...prev, data];
        // FIFO eviction: держим не более MAX_STACK
        return next.length > MAX_STACK ? next.slice(next.length - MAX_STACK) : next;
      });
    });
  }, []);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const dismissAll = useCallback(() => setItems([]), []);

  if (items.length === 0) return null;

  return createPortal(
    <div className="in-app-push-stack" role="region" aria-label="Уведомления">
      {items.length >= 3 && (
        <button type="button" className="in-app-push-dismiss-all" onClick={dismissAll}>
          Закрыть все ({items.length})
        </button>
      )}
      {items.map((p) => (
        <PushCard key={p.id} data={p} onDismiss={dismiss} />
      ))}
    </div>,
    document.body,
  );
}

interface PushCardProps {
  data: InAppPushData;
  onDismiss: (id: number) => void;
}

function PushCard({ data, onDismiss }: PushCardProps) {
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(durationFor(data.severity));
  const startRef = useRef<number>(Date.now());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (paused) {
      const elapsed = Date.now() - startRef.current;
      remainingRef.current = Math.max(0, remainingRef.current - elapsed);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }
    startRef.current = Date.now();
    timeoutRef.current = setTimeout(() => onDismiss(data.id), remainingRef.current);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [paused, onDismiss, data.id]);

  function handleClick() {
    data.onClick?.();
    onDismiss(data.id);
  }

  return (
    <div
      className={`in-app-push in-app-push--${data.severity}${paused ? ' is-paused' : ''}`}
      role="alert"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={handleClick}
    >
      <span className="in-app-push__icon" aria-hidden>
        {data.severity === 'error' ? (
          <AlertCircle size={22} />
        ) : data.severity === 'warning' ? (
          <AlertTriangle size={22} />
        ) : (
          <Bell size={22} />
        )}
      </span>
      <div className="in-app-push__content">
        <div className="in-app-push__title">{data.title}</div>
        {data.body && <div className="in-app-push__body">{data.body}</div>}
      </div>
      <button
        type="button"
        className="in-app-push__close"
        aria-label="Закрыть"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(data.id);
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
